import { t } from 'i18next';
import { urlHelper } from 'src/app/helpers';
import { getFormData } from 'src/app/helpers/form.helper';
import {
  infiniteQueryOptions,
  InitialPageParam,
  onQueryStartedCreate,
  onQueryStartedDelete,
  onQueryStartedUpdate,
} from 'src/app/helpers/redux.helper';
import { apiSlice } from 'src/app/redux/api/apiSlice';
import {
  PersonIdentifiedCreateDataType,
  PersonIdentifiedData,
  PersonIdentifiedLookupDataType,
  PersonIdentifiedLookupResultType,
  PersonIdentifiedUpdateDataType,
  PersonImportDataType,
} from 'src/app/redux/interests/persons/person-slice.type';
import { finalUrlsConfig } from 'src/configs/url.config';
import { MutationType } from 'src/enums/operation.enum';

export const personIdentifiedslice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPersonIdentified: builder.infiniteQuery<
      PersonIdentifiedLookupResultType,
      PersonIdentifiedLookupDataType | void,
      InitialPageParam
    >({
      infiniteQueryOptions,
      query: ({ pageParam, queryArg }) => {
        return {
          url: finalUrlsConfig().interest.identified.person.lookup,
          params: {
            ...pageParam,
            ...queryArg,
          },
        };
      },

      providesTags: [{ type: 'PersonIdentified', id: 'LIST' }],
    }),
    getPersonsIdentifiedAll: builder.query<PersonIdentifiedLookupResultType, PersonIdentifiedLookupDataType | void>({
      query: (params?: PersonIdentifiedLookupDataType) => ({
        url: finalUrlsConfig().interest.identified.person.lookup,
        params: urlHelper.getLookupQuery(params),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.rows.map(({ id }) => ({
                type: 'PersonIdentified' as const,
                id,
              })),
              { type: 'PersonIdentified', id: 'LIST' },
            ]
          : [{ type: 'PersonIdentified', id: 'LIST' }],
    }),
    createPerson: builder.mutation<PersonIdentifiedData, PersonIdentifiedCreateDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.person.create,
        method: 'POST',
        body: getFormData(data, ['pictures'], MutationType.CREATE),
      }),

      onQueryStarted: async (personData, api) => onQueryStartedCreate(personData, api, 'getPersonIdentified'),
      invalidatesTags: [
        // { type: 'PersonIdentified', id: 'LIST' },
        { type: 'Interests', id: 'LIST' },
      ],
    }),
    updatePerson: builder.mutation<PersonIdentifiedData, PersonIdentifiedUpdateDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.person.update,
        body: getFormData(data, ['pictures']),
        method: 'PATCH',
      }),
      onQueryStarted: async ({ id }, api) => onQueryStartedUpdate({ id }, api, 'getPersonIdentified'),
      // invalidatesTags: (_result, _error, data) => [{ type: 'PersonIdentified', id: data.id }],
    }),
    deletePerson: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `${finalUrlsConfig().interest.identified.person.delete}/${id}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (id, api) => onQueryStartedDelete(id, api, 'getPersonIdentified'),
      // invalidatesTags: (_result, _error, id) => [
      //   { type: 'PersonIdentified', id },
      //   { type: 'Interests', id: 'LIST' },
      // ],
    }),
    exportPersonIdentifiedExcell: builder.mutation<ArrayBuffer, { ids?: string[] }>({
      query: (data) => ({
        url: finalUrlsConfig().exports.person,
        method: 'POST',
        body: data,
        responseHandler: async (response: any) => {
          if (!response.ok) {
            const errorResponse = await response.json();
            throw new Error(errorResponse?.message || t('errors.defaultErrorText'));
          }
          return response.blob();
        },
        cache: 'no-cache',
      }),
    }),
    importPerson: builder.mutation<void, PersonImportDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.person.import,
        method: 'POST',
        body: getFormData(data, ['file']),
      }),

      invalidatesTags: [{ type: 'PersonIdentified', id: 'LIST' }],
    }),
  }),
});

export const {
  useCreatePersonMutation,
  useDeletePersonMutation,
  useUpdatePersonMutation,
  useExportPersonIdentifiedExcellMutation,
  useImportPersonMutation,
  useGetPersonIdentifiedInfiniteQuery,
  useGetPersonsIdentifiedAllQuery,
  useLazyGetPersonsIdentifiedAllQuery,
} = personIdentifiedslice;
